import BarChartIcon from '@mui/icons-material/BarChart';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import React from 'react';

import { UserProfile, UserRole } from '../backendsdk';

React;

export interface PageDefs {
    name: string;
    path: string[];
    permissions: Record<string, UserRole[]>;
    mobileSupport: boolean;
    customers?: string[];
    exclude?: [string, UserRole][];
    country?: string;
    new?: boolean;
}

export const flattenRoles = (page: PageDefs) => {
    return Object.values(page.permissions).flat();
};

export const isAllowedCustomer = (pageDef: PageDefs, profile: UserProfile) => {
    const denied =
        pageDef.exclude !== undefined &&
        pageDef.exclude.some(([customer, role]) => {
            return profile.customer.name.toLocaleLowerCase() === customer && profile.role === role;
        });
    const allowed =
        pageDef.customers === undefined ||
        pageDef.customers.includes(profile.customer.name.toLocaleLowerCase()) ||
        profile.role === UserRole.OeAdmin;
    return allowed && !denied;
};

export const fleetOverviewPageDefs: PageDefs = {
    name: 'overview',
    path: ['/overview/:licensePlate/:tripId', '/overview/:licensePlate', '/overview'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev],
        L2: [
            UserRole.OeInsuranceOperator,
            UserRole.FleetAdmin,
            UserRole.FleetExecutive,
            UserRole.FleetSafetyManager,
            UserRole.FleetInsuranceOperator,
        ],
    },
    exclude: [
        ['egged', UserRole.FleetSafetyManager],
        ['egged', UserRole.FleetInsuranceOperator],
        ['egged', UserRole.FleetAdmin],
        ['eggedheseim', UserRole.FleetSafetyManager],
        ['eggedheseim', UserRole.FleetInsuranceOperator],
        ['eggedheseim', UserRole.FleetAdmin],
    ],
};

export const malfunctionsPageDefs: PageDefs = {
    name: 'malfunctions',
    path: ['/tasks/verify', '/tasks/adjust', '/tasks/:malfunctionId/:modeStr?', '/tasks'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [
            UserRole.FleetAdmin,
            UserRole.FleetExecutive,
            UserRole.FleetSafetyManager,
            UserRole.FleetInsuranceOperator,
            UserRole.Technician,
            UserRole.GuestTechnician,
        ],
    },
};

export const fleetDriversPageDefs: PageDefs = {
    name: 'drivers',
    path: ['/fleet_drivers'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
    },
};

export const fleetVehiclesPageDefs: PageDefs = {
    name: 'vehicles',
    path: ['/fleet_vehicles'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
    },
};

export const findVideoPageDefs: PageDefs = {
    name: 'find_video',
    path: ['/find_video/:commandId', '/find_video'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [
            UserRole.FleetAdmin,
            UserRole.FleetExecutive,
            UserRole.FleetSafetyManager,
            UserRole.FleetInsuranceOperator,
            UserRole.FleetOperationsControl,
        ],
    },
    exclude: [
        ['danbadarom', UserRole.FleetSafetyManager],
    ],
};

/** Uniform wellness export browser; backend gates by SSM UNIFORM_WELLNESS_IMAGES_CUSTOMERS and get_clip permissions. */
export const uniformWellnessImagesPageDefs: PageDefs = {
    name: 'uniform_images',
    path: ['/uniform_images/:prefix', '/uniform_images'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [UserRole.FleetAdmin, UserRole.FleetExecutive, UserRole.FleetSafetyManager],
    },
    customers: ['metropoline', 'danbadarom'],
};

export const driversPageDefs: PageDefs = {
    name: 'drivers',
    path: ['/drivers/:driverToken', '/drivers'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [UserRole.FleetAdmin, UserRole.FleetExecutive, UserRole.FleetSafetyManager],
    },
    exclude: [['egged', UserRole.FleetInsuranceOperator]],
};

export const eventFeedPageDefs: PageDefs = {
    name: 'event_feed',
    path: ['/event_feed/:eventId', '/event_feed'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [
            UserRole.FleetAdmin,
            UserRole.FleetExecutive,
            UserRole.FleetSafetyManager,
            UserRole.FleetInsuranceOperator,
        ],
    },
    exclude: [
        ['eggedheseim', UserRole.FleetSafetyManager],
        ['eggedheseim', UserRole.FleetInsuranceOperator],
        ['eggedheseim', UserRole.FleetAdmin],
        ['egged', UserRole.FleetSafetyManager],
        ['egged', UserRole.FleetInsuranceOperator],
        ['egged', UserRole.FleetAdmin],
    ],
    new: true,
};

export const coachingPageDefs: PageDefs = {
    name: 'coaching',
    path: ['/coaching/:sessionId/send', '/coaching/:sessionId/play', '/coaching/:sessionId', '/coaching'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator, UserRole.FleetAdmin],
        L2: [UserRole.FleetExecutive, UserRole.FleetSafetyManager],
    },
    exclude: [['egged', UserRole.FleetInsuranceOperator]],
};

export const eventsPageDefs: PageDefs = {
    name: 'events',
    path: ['/events'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [UserRole.FleetAdmin, UserRole.FleetExecutive, UserRole.FleetSafetyManager],
    },
    exclude: [
        ['egged', UserRole.FleetSafetyManager],
        ['egged', UserRole.FleetInsuranceOperator],
        ['egged', UserRole.FleetAdmin],
        ['eggedheseim', UserRole.FleetSafetyManager],
        ['eggedheseim', UserRole.FleetInsuranceOperator],
        ['eggedheseim', UserRole.FleetAdmin],
    ],
};

export const metricsBaseURL = '/metrics';

const metricsPermissions = {
    L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
    L2: [UserRole.FleetAdmin, UserRole.FleetExecutive, UserRole.FleetSafetyManager, UserRole.FleetInsuranceOperator],
};

export const safetyMetricsPageDefs: PageDefs = {
    name: 'safety_metrics',
    path: [metricsBaseURL, '/metrics/safety'],
    mobileSupport: false,
    permissions: metricsPermissions,
};

export const accidentsMetricsPageDefs: PageDefs = {
    name: 'accidents_metrics',
    path: ['/metrics/accidents'],
    mobileSupport: false,
    permissions: metricsPermissions,
};

export const csaScoresPageDefs: PageDefs = {
    name: 'csa_scores',
    path: ['/metrics/csa_scores'],
    mobileSupport: false,
    permissions: metricsPermissions,
    country: 'us',
    new: true,
};

export const policiesPageDefs: PageDefs = {
    name: 'policies',
    path: ['/policies'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
    },
};

export const accidentsPageDefs: PageDefs = {
    name: 'accidents',
    path: [
        '/accidents/:accidentId/command/:commandId',
        '/accidents/:accidentId/trip/:tripId/:driverTripId',
        '/accidents/:accidentId/trip/:tripId',
        '/accidents/:accidentId',
        '/accidents',
    ],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator, UserRole.FleetAdmin],
        L2: [UserRole.FleetExecutive, UserRole.FleetInsuranceOperator],
        L3: [UserRole.FleetSafetyManager, UserRole.CarShop],
    },
};

export const lossRunsPageDefs: PageDefs = {
    name: 'loss_runs',
    path: ['/loss_runs'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
    },
};

export const dailyDashboardPageDefs: PageDefs = {
    name: 'Daily Dashboard',
    path: ['/daily_dashboard'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [UserRole.FleetAdmin, UserRole.FleetExecutive],
    },
    customers: ['qa', 'tower-ny'],
    new: true,
};

export const usersPageDefs: PageDefs = {
    name: 'users',
    path: ['/users'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.FleetAdmin],
    },
};

export const reportsPageDefs: PageDefs = {
    name: 'reports',
    path: [
        '/reports/:reportType/run/:instanceId',
        '/reports/:reportType/:reportId',
        '/reports/:reportType',
        '/reports',
    ],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator, UserRole.FleetAdmin],
        L2: [UserRole.FleetExecutive, UserRole.FleetSafetyManager],
        L3: [UserRole.FleetInsuranceOperator],
    },
    exclude: [
        ['egged', UserRole.FleetSafetyManager],
        ['egged', UserRole.FleetInsuranceOperator],
        ['egged', UserRole.FleetAdmin],
        ['eggedheseim', UserRole.FleetSafetyManager],
        ['eggedheseim', UserRole.FleetInsuranceOperator],
        ['eggedheseim', UserRole.FleetAdmin],
    ],
    new: true,
};

export const alertsPageDefs: PageDefs = {
    name: 'alerts',
    path: ['/alerts/:reportType/:alertId', '/alerts/:reportType', '/alerts'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator, UserRole.FleetAdmin],
        L2: [UserRole.FleetExecutive, UserRole.FleetSafetyManager],
        L3: [UserRole.FleetInsuranceOperator],
    },
    exclude: [
        ['egged', UserRole.FleetSafetyManager],
        ['egged', UserRole.FleetInsuranceOperator],
        ['egged', UserRole.FleetAdmin],
        ['eggedheseim', UserRole.FleetSafetyManager],
        ['eggedheseim', UserRole.FleetInsuranceOperator],
        ['eggedheseim', UserRole.FleetAdmin],
    ],
    new: true,
};

export const operationsPageDefs: PageDefs = {
    name: 'operations',
    path: ['/operations'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin],
    },
};

export const otaPageDefs: PageDefs = {
    name: 'ota',
    path: ['/ota'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin],
    },
};

export const CustomerConfigPageDefs: PageDefs = {
    name: 'customer_config',
    path: ['/customer_config'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin],
    },
};

export const userSettingsPageDefs: PageDefs = {
    name: 'user_settings',
    path: ['/user_settings'],
    mobileSupport: true,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.OeInsuranceOperator],
        L2: [
            UserRole.FleetAdmin,
            UserRole.FleetExecutive,
            UserRole.FleetSafetyManager,
            UserRole.FleetInsuranceOperator,
            UserRole.CarShop,
            UserRole.Technician,
        ],
    },
};

export const organizationSettingsPageDefs: PageDefs = {
    name: 'organization_settings',
    path: ['/organization_settings'],
    mobileSupport: false,
    permissions: {
        L1: [UserRole.OeAdmin, UserRole.OeDev, UserRole.FleetAdmin],
    },
};

export const sections = ['fleet', 'safety', 'metrics', 'compliance', 'insurance', 'settings', 'internal'];

export type Section = typeof sections[number];

export const pagesInSections: Record<Section, PageDefs[]> = {
    fleet: [fleetOverviewPageDefs, malfunctionsPageDefs, reportsPageDefs, alertsPageDefs, findVideoPageDefs, uniformWellnessImagesPageDefs],
    safety: [eventFeedPageDefs, driversPageDefs, coachingPageDefs, eventsPageDefs],
    metrics: [safetyMetricsPageDefs, accidentsMetricsPageDefs, csaScoresPageDefs],
    insurance: [policiesPageDefs, accidentsPageDefs, lossRunsPageDefs, dailyDashboardPageDefs],
    settings: [usersPageDefs, organizationSettingsPageDefs, fleetDriversPageDefs, fleetVehiclesPageDefs],
    internal: [operationsPageDefs, otaPageDefs, CustomerConfigPageDefs],
};

export const getSectionIcons = (fontSize = 18): Record<Section, JSX.Element> => {
    return {
        fleet: <LocalShippingIcon sx={{ fontSize: fontSize }} />,
        safety: <VerifiedUserIcon sx={{ fontSize: fontSize }} />,
        metrics: <BarChartIcon sx={{ fontSize: fontSize }} />,
        compliance: <ContentPasteIcon sx={{ fontSize: fontSize }} />,
        insurance: <UmbrellaIcon sx={{ fontSize: fontSize, transform: 'rotate(180deg)' }} />,
        settings: <SettingsIcon sx={{ fontSize: fontSize }} />,
        internal: <HomeRepairServiceIcon sx={{ fontSize: fontSize }} />,
    };
};
